import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building2, Plus, Search, Users, Link2, Unlink, 
  Eye, Edit, Trash2, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Mail, Phone, UserCog, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const EscritoriosPage = () => {
  const { api } = useAuth();
  
  const [escritorios, setEscritorios] = useState([]);
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modais
  const [showNovoEscritorio, setShowNovoEscritorio] = useState(false);
  const [showVincularEmpresa, setShowVincularEmpresa] = useState(false);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [showEditarEscritorio, setShowEditarEscritorio] = useState(false);
  const [escritorioSelecionado, setEscritorioSelecionado] = useState(null);
  const [empresasEscritorio, setEmpresasEscritorio] = useState([]);
  
  // Formulário novo escritório
  const [novoEscritorio, setNovoEscritorio] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    email: '',
    telefone: '',
    limite_empresas: 10,
    limite_usuarios: 5,
    admin_email: '',
    admin_nome: '',
    admin_senha: ''
  });

  // Formulário edição escritório
  const [editEscritorio, setEditEscritorio] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    email: '',
    telefone: '',
    endereco: '',
    limite_empresas: 10,
    limite_usuarios: 5
  });
  
  // Dados do admin do escritório
  const [adminEscritorio, setAdminEscritorio] = useState(null);
  const [editAdmin, setEditAdmin] = useState({
    nome: '',
    email: '',
    nova_senha: ''
  });
  const [carregandoAdmin, setCarregandoAdmin] = useState(false);
  
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [escResponse, empResponse] = await Promise.all([
        api.get('/escritorios'),
        api.get('/empresas?incluir_teste=true')  // Incluir empresas de teste para gestão
      ]);
      setEscritorios(escResponse.data || []);
      setEmpresasDisponiveis(empResponse.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '-';
    const numeros = cnpj.replace(/\D/g, '');
    return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const criarEscritorio = async () => {
    if (!novoEscritorio.razao_social || !novoEscritorio.cnpj) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      setSalvando(true);
      
      // Criar escritório
      const escResponse = await api.post('/escritorios', {
        razao_social: novoEscritorio.razao_social,
        nome_fantasia: novoEscritorio.nome_fantasia,
        cnpj: novoEscritorio.cnpj.replace(/\D/g, ''),
        email: novoEscritorio.email,
        telefone: novoEscritorio.telefone,
        limite_empresas: novoEscritorio.limite_empresas,
        limite_usuarios: novoEscritorio.limite_usuarios
      });

      const escritorioId = escResponse.data.id;

      // Criar usuário admin do escritório se informado
      if (novoEscritorio.admin_email && novoEscritorio.admin_nome && novoEscritorio.admin_senha) {
        try {
          await api.post('/usuarios', {
            email: novoEscritorio.admin_email,
            nome: novoEscritorio.admin_nome,
            perfil: 'administrador_escritorio',
            password: novoEscritorio.admin_senha,
            escritorio_id: escritorioId
          });
          toast.success('Escritório e administrador criados com sucesso!');
        } catch (userError) {
          toast.warning('Escritório criado, mas erro ao criar usuário admin');
          console.error(userError);
        }
      } else {
        toast.success('Escritório criado com sucesso!');
      }

      setShowNovoEscritorio(false);
      setNovoEscritorio({
        razao_social: '',
        nome_fantasia: '',
        cnpj: '',
        email: '',
        telefone: '',
        limite_empresas: 10,
        limite_usuarios: 5,
        admin_email: '',
        admin_nome: '',
        admin_senha: ''
      });
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar escritório');
      console.error(error);
    } finally {
      setSalvando(false);
    }
  };

  const abrirDetalhes = async (escritorio) => {
    setEscritorioSelecionado(escritorio);
    setShowDetalhes(true);
    
    try {
      const response = await api.get(`/escritorios/${escritorio.id}/empresas`);
      setEmpresasEscritorio(response.data || []);
    } catch (error) {
      console.error(error);
      setEmpresasEscritorio([]);
    }
  };

  const abrirVincularEmpresa = (escritorio) => {
    setEscritorioSelecionado(escritorio);
    setShowVincularEmpresa(true);
  };

  const vincularEmpresa = async (empresaId) => {
    if (!escritorioSelecionado) return;

    try {
      await api.post(`/escritorios/${escritorioSelecionado.id}/empresas/${empresaId}`);
      toast.success('Empresa vinculada ao escritório!');
      
      // Atualizar lista de empresas do escritório
      const response = await api.get(`/escritorios/${escritorioSelecionado.id}/empresas`);
      setEmpresasEscritorio(response.data || []);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular empresa');
      console.error(error);
    }
  };

  const desvincularEmpresa = async (empresaId) => {
    if (!escritorioSelecionado) return;

    try {
      await api.delete(`/escritorios/${escritorioSelecionado.id}/empresas/${empresaId}`);
      toast.success('Empresa desvinculada do escritório');
      
      // Atualizar lista
      const response = await api.get(`/escritorios/${escritorioSelecionado.id}/empresas`);
      setEmpresasEscritorio(response.data || []);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desvincular empresa');
      console.error(error);
    }
  };

  const abrirEditarEscritorio = async (escritorio) => {
    setEscritorioSelecionado(escritorio);
    setEditEscritorio({
      razao_social: escritorio.razao_social || '',
      nome_fantasia: escritorio.nome_fantasia || '',
      cnpj: escritorio.cnpj || '',
      email: escritorio.email || '',
      telefone: escritorio.telefone || '',
      endereco: escritorio.endereco || '',
      limite_empresas: escritorio.limite_empresas || 10,
      limite_usuarios: escritorio.limite_usuarios || 5
    });
    setShowEditarEscritorio(true);
    
    // Buscar admin do escritório
    setCarregandoAdmin(true);
    try {
      const response = await api.get(`/escritorios/${escritorio.id}/admin`);
      if (response.data) {
        setAdminEscritorio(response.data);
        setEditAdmin({
          nome: response.data.nome || '',
          email: response.data.email || '',
          nova_senha: ''
        });
      } else {
        setAdminEscritorio(null);
        setEditAdmin({ nome: '', email: '', nova_senha: '' });
      }
    } catch (error) {
      console.error('Erro ao buscar admin:', error);
      setAdminEscritorio(null);
      setEditAdmin({ nome: '', email: '', nova_senha: '' });
    } finally {
      setCarregandoAdmin(false);
    }
  };

  const salvarEdicaoEscritorio = async () => {
    if (!escritorioSelecionado) return;

    try {
      setSalvando(true);
      
      // Atualizar dados do escritório
      await api.put(`/escritorios/${escritorioSelecionado.id}`, editEscritorio);
      
      // Se tem admin e dados foram alterados, atualizar admin também
      if (adminEscritorio && (editAdmin.nome || editAdmin.email || editAdmin.nova_senha)) {
        const adminUpdate = {};
        if (editAdmin.nome && editAdmin.nome !== adminEscritorio.nome) {
          adminUpdate.nome = editAdmin.nome;
        }
        if (editAdmin.email && editAdmin.email !== adminEscritorio.email) {
          adminUpdate.email = editAdmin.email;
        }
        if (editAdmin.nova_senha) {
          adminUpdate.nova_senha = editAdmin.nova_senha;
        }
        
        if (Object.keys(adminUpdate).length > 0) {
          await api.put(`/usuarios/${adminEscritorio.id}`, adminUpdate);
        }
      }
      
      toast.success('Escritório atualizado com sucesso!');
      setShowEditarEscritorio(false);
      setEscritorioSelecionado(null);
      setAdminEscritorio(null);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar escritório');
      console.error(error);
    } finally {
      setSalvando(false);
    }
  };

  const getEmpresasNaoVinculadas = () => {
    const idsVinculados = empresasEscritorio.map(e => e.id);
    return empresasDisponiveis.filter(e => 
      !idsVinculados.includes(e.id) && 
      (!e.escritorio_id || e.tipo_gestao !== 'escritorio')
    );
  };

  const escritoriosFiltrados = escritorios.filter(e => {
    const termo = searchTerm.toLowerCase();
    return (
      e.razao_social?.toLowerCase().includes(termo) ||
      e.nome_fantasia?.toLowerCase().includes(termo) ||
      e.cnpj?.includes(termo)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plataforma-escritorios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Escritórios</h1>
          <p className="text-slate-600 mt-1">
            Gerencie escritórios de contabilidade e seus vínculos com empresas
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={carregarDados}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setShowNovoEscritorio(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Escritório
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{escritorios.length}</p>
                <p className="text-sm text-slate-500">Escritórios</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Link2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {empresasDisponiveis.filter(e => e.tipo_gestao === 'escritorio').length}
                </p>
                <p className="text-sm text-slate-500">Empresas Vinculadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {empresasDisponiveis.filter(e => e.tipo_gestao !== 'escritorio').length}
                </p>
                <p className="text-sm text-slate-500">Empresas Independentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de Escritórios */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Escritórios</CardTitle>
          <CardDescription>
            {escritoriosFiltrados.length} escritório(s) cadastrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {escritoriosFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum escritório encontrado</p>
              <p className="text-sm">Clique em "Novo Escritório" para criar</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Escritório</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead className="text-center">Empresas</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {escritoriosFiltrados.map((escritorio) => {
                    const empresasCount = empresasDisponiveis.filter(
                      e => e.escritorio_id === escritorio.id
                    ).length;
                    
                    return (
                      <TableRow key={escritorio.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {escritorio.nome_fantasia || escritorio.razao_social}
                              </p>
                              {escritorio.nome_fantasia && (
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                  {escritorio.razao_social}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {formatCNPJ(escritorio.cnpj)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {escritorio.email && (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Mail className="w-3 h-3" />
                                {escritorio.email}
                              </div>
                            )}
                            {escritorio.telefone && (
                              <div className="flex items-center gap-1 text-slate-500">
                                <Phone className="w-3 h-3" />
                                {escritorio.telefone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">
                            {empresasCount} / {escritorio.limite_empresas || 10}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={escritorio.status === 'ativo' ? 'default' : 'secondary'}>
                            {escritorio.status === 'ativo' ? 'Ativo' : 'Pendente'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => abrirDetalhes(escritorio)}
                              title="Ver detalhes"
                              data-testid={`ver-detalhes-escritorio-${escritorio.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => abrirEditarEscritorio(escritorio)}
                              title="Editar escritório"
                              data-testid={`editar-escritorio-${escritorio.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => abrirVincularEmpresa(escritorio)}
                              title="Vincular empresa"
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Novo Escritório */}
      <Dialog open={showNovoEscritorio} onOpenChange={setShowNovoEscritorio}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Escritório de Contabilidade</DialogTitle>
            <DialogDescription>
              Cadastre um novo escritório e opcionalmente crie o usuário administrador
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Dados do Escritório */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 border-b pb-2">Dados do Escritório</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Razão Social *</Label>
                  <Input
                    value={novoEscritorio.razao_social}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, razao_social: e.target.value})}
                    placeholder="Razão social do escritório"
                  />
                </div>
                <div>
                  <Label>Nome Fantasia</Label>
                  <Input
                    value={novoEscritorio.nome_fantasia}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, nome_fantasia: e.target.value})}
                    placeholder="Nome fantasia"
                  />
                </div>
                <div>
                  <Label>CNPJ *</Label>
                  <Input
                    value={novoEscritorio.cnpj}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, cnpj: e.target.value})}
                    placeholder="00.000.000/0000-00"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={novoEscritorio.email}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, email: e.target.value})}
                    placeholder="contato@escritorio.com.br"
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={novoEscritorio.telefone}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, telefone: e.target.value})}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <Label>Limite de Empresas</Label>
                  <Input
                    type="number"
                    value={novoEscritorio.limite_empresas}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, limite_empresas: parseInt(e.target.value) || 10})}
                    min={1}
                  />
                </div>
                <div>
                  <Label>Limite de Usuários</Label>
                  <Input
                    type="number"
                    value={novoEscritorio.limite_usuarios}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, limite_usuarios: parseInt(e.target.value) || 5})}
                    min={1}
                  />
                </div>
              </div>
            </div>

            {/* Usuário Admin */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 border-b pb-2">
                Usuário Administrador (opcional)
              </h4>
              <p className="text-sm text-slate-500">
                Crie automaticamente um usuário administrador para o escritório
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Admin</Label>
                  <Input
                    value={novoEscritorio.admin_nome}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, admin_nome: e.target.value})}
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <Label>Email do Admin</Label>
                  <Input
                    type="email"
                    value={novoEscritorio.admin_email}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, admin_email: e.target.value})}
                    placeholder="admin@escritorio.com.br"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Senha do Admin</Label>
                  <Input
                    type="password"
                    value={novoEscritorio.admin_senha}
                    onChange={(e) => setNovoEscritorio({...novoEscritorio, admin_senha: e.target.value})}
                    placeholder="Senha inicial"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoEscritorio(false)}>
              Cancelar
            </Button>
            <Button onClick={criarEscritorio} disabled={salvando}>
              {salvando ? 'Criando...' : 'Criar Escritório'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Vincular Empresa */}
      <Dialog open={showVincularEmpresa} onOpenChange={setShowVincularEmpresa}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vincular Empresa ao Escritório</DialogTitle>
            <DialogDescription>
              {escritorioSelecionado?.nome_fantasia || escritorioSelecionado?.razao_social}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <h4 className="font-medium text-slate-700">Empresas Disponíveis</h4>
            {getEmpresasNaoVinculadas().length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p>Todas as empresas já estão vinculadas</p>
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {getEmpresasNaoVinculadas().map((empresa) => (
                  <div 
                    key={empresa.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{empresa.nome_fantasia || empresa.razao_social}</p>
                        <p className="text-xs text-slate-500">{formatCNPJ(empresa.cnpj)}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => vincularEmpresa(empresa.id)}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Vincular
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVincularEmpresa(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Detalhes do Escritório
            </DialogTitle>
          </DialogHeader>
          
          {escritorioSelecionado && (
            <div className="space-y-6 py-4">
              {/* Dados básicos */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Razão Social</p>
                  <p className="font-medium">{escritorioSelecionado.razao_social}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Nome Fantasia</p>
                  <p className="font-medium">{escritorioSelecionado.nome_fantasia || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">CNPJ</p>
                  <p className="font-medium">{formatCNPJ(escritorioSelecionado.cnpj)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge variant={escritorioSelecionado.status === 'ativo' ? 'default' : 'secondary'}>
                    {escritorioSelecionado.status === 'ativo' ? 'Ativo' : 'Pendente'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{escritorioSelecionado.email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telefone</p>
                  <p className="font-medium">{escritorioSelecionado.telefone || '-'}</p>
                </div>
              </div>

              {/* Limites */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {empresasEscritorio.length} / {escritorioSelecionado.limite_empresas || 10}
                  </p>
                  <p className="text-sm text-slate-500">Empresas</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {escritorioSelecionado.limite_usuarios || 5}
                  </p>
                  <p className="text-sm text-slate-500">Limite Usuários</p>
                </div>
              </div>

              {/* Empresas Vinculadas */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900">Empresas Vinculadas</h4>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setShowDetalhes(false);
                      abrirVincularEmpresa(escritorioSelecionado);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Vincular
                  </Button>
                </div>
                
                {empresasEscritorio.length === 0 ? (
                  <div className="text-center py-6 text-slate-500 border rounded-lg">
                    <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma empresa vinculada</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {empresasEscritorio.map((empresa) => (
                      <div 
                        key={empresa.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {empresa.nome_fantasia || empresa.razao_social}
                            </p>
                            <p className="text-xs text-slate-500">{formatCNPJ(empresa.cnpj)}</p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => desvincularEmpresa(empresa.id)}
                        >
                          <Unlink className="w-4 h-4 mr-1" />
                          Desvincular
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetalhes(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Escritório */}
      <Dialog open={showEditarEscritorio} onOpenChange={setShowEditarEscritorio}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-editar-escritorio">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Editar Escritório
            </DialogTitle>
            <DialogDescription>
              Atualize os dados cadastrais do escritório
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* Dados do Escritório */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700 border-b pb-2">Dados do Escritório</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Razão Social *</Label>
                  <Input
                    data-testid="edit-razao-social-escritorio"
                    value={editEscritorio.razao_social}
                    onChange={(e) => setEditEscritorio({...editEscritorio, razao_social: e.target.value})}
                    placeholder="Razão social do escritório"
                  />
                </div>
                <div>
                  <Label>Nome Fantasia</Label>
                  <Input
                    data-testid="edit-nome-fantasia-escritorio"
                    value={editEscritorio.nome_fantasia}
                    onChange={(e) => setEditEscritorio({...editEscritorio, nome_fantasia: e.target.value})}
                    placeholder="Nome fantasia"
                  />
                </div>
                <div>
                  <Label>CNPJ</Label>
                  <Input
                    data-testid="edit-cnpj-escritorio"
                    value={editEscritorio.cnpj}
                    onChange={(e) => setEditEscritorio({...editEscritorio, cnpj: e.target.value})}
                    placeholder="00.000.000/0001-00"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    data-testid="edit-email-escritorio"
                    type="email"
                    value={editEscritorio.email}
                    onChange={(e) => setEditEscritorio({...editEscritorio, email: e.target.value})}
                    placeholder="contato@escritorio.com.br"
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    data-testid="edit-telefone-escritorio"
                    value={editEscritorio.telefone}
                    onChange={(e) => setEditEscritorio({...editEscritorio, telefone: e.target.value})}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Endereço</Label>
                  <Input
                    data-testid="edit-endereco-escritorio"
                    value={editEscritorio.endereco}
                    onChange={(e) => setEditEscritorio({...editEscritorio, endereco: e.target.value})}
                    placeholder="Endereço completo"
                  />
                </div>
                <div>
                  <Label>Limite de Empresas</Label>
                  <Input
                    data-testid="edit-limite-empresas"
                    type="number"
                    value={editEscritorio.limite_empresas}
                    onChange={(e) => setEditEscritorio({...editEscritorio, limite_empresas: parseInt(e.target.value) || 10})}
                    min="1"
                  />
                </div>
                <div>
                  <Label>Limite de Usuários</Label>
                  <Input
                    data-testid="edit-limite-usuarios"
                    type="number"
                    value={editEscritorio.limite_usuarios}
                    onChange={(e) => setEditEscritorio({...editEscritorio, limite_usuarios: parseInt(e.target.value) || 5})}
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Dados do Administrador */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700 border-b pb-2 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Usuário Administrador
              </h4>
              
              {carregandoAdmin ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-500">Carregando dados do admin...</span>
                </div>
              ) : adminEscritorio ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome do Admin</Label>
                    <Input
                      data-testid="edit-admin-nome"
                      value={editAdmin.nome}
                      onChange={(e) => setEditAdmin({...editAdmin, nome: e.target.value})}
                      placeholder="Nome completo"
                    />
                  </div>
                  <div>
                    <Label>Email do Admin</Label>
                    <Input
                      data-testid="edit-admin-email"
                      type="email"
                      value={editAdmin.email}
                      onChange={(e) => setEditAdmin({...editAdmin, email: e.target.value})}
                      placeholder="admin@escritorio.com.br"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Nova Senha (deixe em branco para manter)</Label>
                    <Input
                      data-testid="edit-admin-senha"
                      type="password"
                      value={editAdmin.nova_senha}
                      onChange={(e) => setEditAdmin({...editAdmin, nova_senha: e.target.value})}
                      placeholder="Digite apenas se quiser alterar a senha"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Se informar nova senha, o usuário será obrigado a trocar no próximo login.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg">
                  <UserCog className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p>Nenhum administrador cadastrado para este escritório.</p>
                  <p className="text-xs mt-1">O admin pode ser criado na página de Usuários.</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarEscritorio(false)} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={salvarEdicaoEscritorio} disabled={salvando} data-testid="btn-salvar-escritorio">
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EscritoriosPage;
